<template>
  <div class="sidebar-menu-div">
    <el-input
      v-if="isSearchable"
      :placeholder="$i18n.t('Search')"
      :value="searchText"
      prefix-icon="el-icon-search"
      :readonly="isLoading"
      @input="search"
    />
    <div v-if="needBackButton" class="back-button-container" @click="backBtn">
      <span class="material-icons">arrow_back_ios_new</span>
      <span>{{ $i18n.t(backLabel) }}</span>
    </div>
    <skeleton-generator v-if="isLoading" :schema="skeletonSchema" :loading="true" />
    <ul v-else class="menu" ref="menu">
      <li
        v-if="item.isActive"
        v-for="(item, index) in shownItems"
        :key="index"
        @click="setMenu(item, index)"
        :class="item.className"
      >
        <el-badge :hidden="!item.info" :value="item.info" :key="item.name" class="d-flex" type="success" :max="99">
          <a role="button" v-if="isParent(item)">
            <div>
              <icon v-if="item.icon" :icon="item.icon" class="iconify-icons" />
              <span class="material-icons transparent-icon" v-else>arrow_forward_ios</span>
              <p>{{ $i18n.t(item.name) }}</p>
            </div>
            <span class="material-icons">expand_more</span>
          </a>
          <template v-else-if="item.url && item.url.length">
            <a role="button" v-if="item.isExternalUrl" :href="getUrl(item)" target="_blank">
              <div>
                <icon v-if="item.icon" :icon="item.icon" class="iconify-icons" />
                <span class="material-icons transparent-icon" v-else>arrow_forward_ios</span>
                <p>{{ $i18n.t(item.name) }}</p>
              </div>
            </a>
            <router-link v-else :to="getUrl(item)" :target="getTarget(item)" class="w-100" role="button">
              <div>
                <icon v-if="item.icon" :icon="item.icon" class="iconify-icons" />
                <span class="material-icons transparent-icon" v-else>arrow_forward_ios</span>
                <p>{{ $i18n.t(item.name) }}</p>
              </div>
            </router-link>
          </template>
        </el-badge>
      </li>
    </ul>
    <Version/>
  </div>
</template>

<script>
import { get, isEmpty, uniqBy } from 'lodash'
import menuMixin from '../../utils/mixin-menu'
import { mapActions } from 'vuex'
import SkeletonGenerator from '@/components/SkeletonGenerator.vue'
import Version from '@/components/version/Version.vue'

/**
 * @class DropdownMenu
 *
 * @description dropdown menu functionality for side menu
 */
export default {
  name: 'DropdownMenu',
  components: { Version, SkeletonGenerator },
  mixins: [menuMixin],
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    mainMenu: {
      type: Boolean,
      default: false,
    },
    activeComponent: {
      type: String,
      default: '',
    },
    position: {
      type: String,
      default: 'left',
      required: false,
    },
  },
  data() {
    return {
      menuItems: [],
      shownItems: [],
      levels: [],
      backLabel: '',
      searchText: '',
      searchResult: [],
      infoCount: 0,
      counter: 0,
      mainCounter: 0,
      isLoading: true,
      skeletonSchema: [
        { width: '65%', icon: true },
        { width: '60%', icon: true },
        { width: '55%', icon: false },
        { width: '85%', icon: true },
        { width: '70%', icon: true },
        { width: '80%', icon: true },
        { width: '70%', icon: false },
      ],
    }
  },
  computed: {
    isSearchable() {
      return this.searchable
    },
    needBackButton() {
      return !isEmpty(this.levels) && !this.searchText
    },
  },
  methods: {
    ...mapActions(['showModal', 'hideModal']),
    /**
     *
     * @description Menü betöltése
     * @returns {Promise<void>}
     */
    async load() {
      await this.$client
        .execute({
          operationId: 'Menu_filter',
          parameters: {
            filterOptions: JSON.stringify({
              where: {
                subSystemCode: window.CORE.subSystem.code,
                mainMenu: this.mainMenu,
              },
            }),
          },
        })
        .then((result) => {
          this.menuItems = result.body.itemsEditor
          this.bindUrlParams(this.menuItems)
          this.menuItems.map((menu) => {
            this.mainCounter = 0
            if (menu.children && !menu.info) {
              this.populateInfo(menu.children)
              menu.info = this.mainCounter
            }
            if (menu.info) {
              this.infoCount += menu.info
            }
          })
          this.isLoading = false
          this.setShownItems()
        })
    },
    getUrl(item) {
      const forcedProtocol = 'https'
      const url = get(item, 'url', '').trim()
      const isExternal = get(item, 'isExternalUrl', false)
      if (!url) return null
      if (isExternal) {
        if (!url.startsWith(`${forcedProtocol}://`)) {
          return `${forcedProtocol}://` + url
        }
      }
      return url && url.startsWith('/') ? url : '/' + url
    },
    getTarget(item) {
      return item.isExternalUrl || item.isTargetBlank ? '_blank' : '_self'
    },
    bindUrlParams(items) {
      if (!isEmpty(items)) {
        items.forEach((item) => {
          if (!isEmpty(item.parameters)) {
            item.parameters.forEach((param) => {
              if (param.key && param.key.length) item.url = this.addUrlParam(item.url, param)
            })
          }
          if (!isEmpty(item.children)) {
            this.bindUrlParams(item.children)
          }
        })
      }
    },
    addUrlParam(url, param) {
      let { key, value } = param
      if (!value) return url

      const replaceCoreParam = value.startsWith(':')
      const processableVariables = ['CORE.sqlParams', 'CORE.params']

      if (replaceCoreParam) {
        value = value.replace(':', '')
        value = get(CORE, `params.${value}`, value)
      } else {
        processableVariables.forEach((variable) => {
          if (`${value}`.startsWith(variable)) {
            value = get(window, value, value)
          }
        })
      }

      if (url) url += (url.split('?')[1] ? '&' : '?') + `${key}=${value}`
      return url
    },
    setShownItems(items) {
      if (items) {
        this.shownItems = items
        return
      }
      if (isEmpty(this.levels)) {
        this.shownItems = this.menuItems
      } else {
        let path = ''
        this.levels.forEach((index) => {
          path += `.${index}.children`
        })
        path = path.substr(1)
        this.backLabel = get(this.menuItems, path.replace(new RegExp('children$'), 'name'))
        this.shownItems = get(this.menuItems, path)
      }
      this.isLoading = false
    },
    setMenu(item, index) {
      if (this.isParent(item)) {
        this.levels.push(index)
        this.setShownItems()
      }
    },
    backBtn() {
      this.levels.pop()
      this.setShownItems()
    },
    containsText(nodes) {
      if (isEmpty(nodes)) return false
      nodes.forEach((item) => {
        if (!item.isActive) return
        if (isEmpty(item.children)) {
          const translated = this.$i18n.t(item.name)
          if (translated.toLowerCase().includes(this.searchText.toLowerCase())) {
            this.searchResult.push(item)
          }
        } else {
          this.containsText(item.children)
        }
      })
    },
    /**
     *
     * @description Információk hozzárendelése a menüelemekhez
     * @param items
     */
    populateInfo(items) {
      this.counter = 0
      items.map((item) => {
        if (isEmpty(item.children) && item.info) {
          this.counter += item.info
        } else if (isEmpty(item.children) && !item.info) {
          this.counter += 0
        } else {
          this.populateInfo(item.children)
          item.info = this.counter
        }
      })
      if (this.counter) {
        this.mainCounter = this.counter
      }
    },
    search(event) {
      this.searchText = event
      if (this.searchText === '') {
        this.setShownItems()
        return
      }
      this.searchResult = []
      this.containsText(this.menuItems)
      const uniqueResult = uniqBy(this.searchResult, 'url')
      this.setShownItems(uniqueResult)
    },
  },
  created() {
    if (!isEmpty(this.items)) {
      this.menuItems = this.items
      this.setShownItems()
    }
  },
  mounted() {
    if (isEmpty(this.menuItems)) {
      this.load()
    }
  },
  watch: {
    infoCount: {
      handler(value) {
        this.$emit('info-count', value)
      },
    },
  },
}
</script>

<style lang="scss">
.menu .el-badge__content.is-fixed {
  top: 12px;
  right: 15px;
  transform: unset;
}
</style>

<style lang="scss" scoped>
@import 'src/assets/sass/variables';
.sidebar-menu-div {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.menu {
  height: 100%;
  overflow-y: auto;
  font-family: 'Montserrat';
  list-style-type: none;
  padding: 0;
  &::-webkit-scrollbar {
    display: none;
  }
}
.menu li a div {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  p {
    margin: 0;
    padding: 0;
    font-size: 14px;
    color: $icon-large;
  }
}
.back-button-container {
  font-family: 'Montserrat';
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
  color: $icon-large;
  padding: 10px 0px;
  .material-icons {
    font-size: 10px;
  }
  &:hover {
    cursor: pointer;
  }
}
.menu li.parent a div .material-icons {
  color: $icon-large;
}
.transparent-icon {
  color: transparent !important;
}
.menu li.parent a {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: row;
  text-decoration: none;
  padding-right: 5px;
  width: 100%;
  cursor: pointer;
}
.menu li {
  transition: 0.5s;
  &:hover {
    padding-left: 10px;
    background-color: $color-text-regular;
    color: $icon-large;
  }
}
.menu a {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: $icon-large;
  text-decoration: none;
  line-height: 3;
  &:hover {
    font-weight: 600;
    color: $white;
  }
}
</style>
